import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useCallback} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dialog} from '../../../components/common/dialogs/dialog';
import {DialogContent} from '../../../components/common/dialogs/dialogContent';
import {DialogFooter} from '../../../components/common/dialogs/dialogFooter';
import {DialogHeader} from '../../../components/common/dialogs/dialogHeader';
import {useClipboard} from '../../../helpers/hooks/clipboardHookHelpers';
import {ExpressionDryRunSnapshot} from './expressionModel';
import {ResultTypesEnum} from './infer/internal';

/*
 * Constants.
 */

const dialogWidth = 800; // px.
const dialogMaxHeight = '80%';

/*
 * Props.
 */

interface ExpressionDryRunViewerProps {
  dryRun: ExpressionDryRunSnapshot;
  title: ReactNode;
  onRequestClose: Handler;
}

/*
 * Styles.
 */

const StyledDialogContent = styled(DialogContent)`
  overflow-y: auto;
`;

const StyledPre = styled.pre`
  white-space: pre-wrap;
  user-select: text;
`;

/*
 * Component.
 */

export const ExpressionDryRunViewer: FC<ExpressionDryRunViewerProps> = ({dryRun, title, onRequestClose}) => {
  const {copy} = useClipboard();
  const text = renderText(dryRun);

  const copyText = useCallback(() => {
    copy(text);
  }, [copy, text]);

  return (
    <Dialog onRequestClose={onRequestClose} width={dialogWidth} maxHeight={dialogMaxHeight}>
      <DialogHeader title={title} onRequestClose={onRequestClose} />
      <StyledDialogContent>
        <StyledPre>{text}</StyledPre>
      </StyledDialogContent>
      <DialogFooter>
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={copyText}>
          <Trans id="expression-dryrun-copy">Copy</Trans>
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

/*
 * Helpers.
 */

function renderText(snapshot: ExpressionDryRunSnapshot) {
  if (snapshot.type !== ResultTypesEnum.JSON) {
    return snapshot.value;
  }

  // Try to pretty-print JSON
  try {
    const json = JSON.parse(snapshot.value);
    return JSON.stringify(json, null, 2);
  } catch (err) {
    return snapshot.value;
  }
}
